import { useEffect, useState } from 'react';
import { monitorApi, reportsApi, type CustomerSignal, type SessionSummary, type FinanceAlert } from '../api/client.js';
import { EmptyState, ErrorBanner, Spinner } from '../components/States.js';
import ScopeBar from '../components/ScopeBar.js';
import { formatAmount } from '../components/Layout.js';
import { roleCan, permHint } from '../utils/permissions.js';

const SIGNAL_META: Record<string, { label: string; badge: string; card: string }> = {
  churn_risk: { label: '流失风险', badge: 'bg-red-100 text-red-700', card: 'border-red-200' },
  renewal: { label: '续费意向', badge: 'bg-green-100 text-green-700', card: 'border-green-200' },
  upgrade: { label: '增项商机', badge: 'bg-blue-100 text-blue-700', card: 'border-blue-200' },
};

const ALERT_META: Record<string, { label: string; badge: string; card: string }> = {
  receivable: { label: '应收超期', badge: 'bg-red-100 text-red-700', card: 'border-l-red-400' },
  payable: { label: '应付超期', badge: 'bg-violet-100 text-violet-700', card: 'border-l-violet-400' },
};

const ALERT_STATUS: Record<string, { label: string; cls: string }> = {
  unread: { label: '待处理', cls: 'bg-red-50 text-red-600' },
  read: { label: '已读', cls: 'bg-slate-100 text-slate-500' },
  actioned: { label: '已处理', cls: 'bg-green-100 text-green-700' },
};

const ALERT_TABS = [
  { key: 'unread', label: '待处理' },
  { key: 'read', label: '已读' },
  { key: 'actioned', label: '已处理' },
  { key: '', label: '全部' },
];

const STATUS_TABS = [
  { key: 'unread', label: '未处理' },
  { key: 'read', label: '已读' },
  { key: 'actioned', label: '已跟进' },
  { key: '', label: '全部' },
];

export default function SignalCenter() {
  const canIngest = roleCan('monitor:ingest');
  const canHandle = roleCan('signal:handle');
  const canScan = roleCan('aging:scan');
  const [status, setStatus] = useState('unread');
  const [signals, setSignals] = useState<CustomerSignal[]>([]);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // 财务超期预警（账龄扫描产出）
  const [alerts, setAlerts] = useState<FinanceAlert[]>([]);
  const [alertStatus, setAlertStatus] = useState('unread');
  const [alertSide, setAlertSide] = useState('');
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  // 8.4 数据可见范围
  const [scope, setScope] = useState('all');
  const [scopeInfo, setScopeInfo] = useState<{ mode: 'all' | 'mine' | 'owner'; limited: boolean } | null>(null);

  // 消息摄入演示表单
  const [staff, setStaff] = useState('accountant-01');
  const [customer, setCustomer] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);

  const load = async (s: string = status) => {
    setError('');
    try {
      const [sigRes, sessRes, alertRes] = await Promise.all([
        monitorApi.signals(s ? { status: s } : {}),
        monitorApi.sessions(),
        reportsApi.agingAlerts({ status: alertStatus || undefined, side: alertSide || undefined, scope: scope !== 'all' ? scope : undefined }),
      ]);
      setSignals(sigRes.items);
      setSessions(sessRes);
      setAlerts(alertRes.items);
      setScopeInfo(alertRes.scopeInfo ?? null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAlertsLoading(false);
    }
  };

  const loadAlerts = async (st: string = alertStatus) => {
    setError('');
    setAlertsLoading(true);
    try {
      const res = await reportsApi.agingAlerts({ status: st || undefined, side: alertSide || undefined, scope: scope !== 'all' ? scope : undefined });
      setAlerts(res.items);
      setScopeInfo(res.scopeInfo ?? null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => { load(status); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);
  useEffect(() => { loadAlerts(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [alertStatus, alertSide, scope]);

  const scanAging = async () => {
    setScanning(true);
    setNotice('');
    setError('');
    try {
      const r = await reportsApi.agingScan({});
      setNotice(
        `账龄扫描完成：新增 ${r.created} 条、刷新 ${r.updated} 条、已结清自动销号 ${r.resolved} 条` +
        `（当前超期 ${r.overdueCount} 笔 ¥${formatAmount(r.overdueTotal)}）`
      );
      if (alertStatus === 'actioned' || alertStatus === 'read') setAlertStatus('');
      else await loadAlerts();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setScanning(false);
    }
  };

  const markAlert = async (id: string, next: 'read' | 'actioned') => {
    try {
      await reportsApi.agingAlertStatus(id, next);
      await loadAlerts();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportCollectionCsv = async () => {
    setExporting(true);
    setError('');
    try {
      await reportsApi.agingAlertsExport({ status: alertStatus || undefined, side: alertSide || undefined, scope: scope !== 'all' ? scope : undefined });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const markStatus = async (id: string, next: 'read' | 'actioned') => {
    try {
      await monitorApi.updateSignalStatus(id, next, 'manager-01');
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const ingest = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setNotice('');
    setError('');
    try {
      const res = await monitorApi.ingestMessage({
        staffUserId: staff,
        customerUserId: customer,
        content,
      });
      const parts: string[] = [];
      if (res.signals.length > 0) {
        parts.push(`识别到 ${res.signals.length} 个客户信号：${res.signals.map((s) => SIGNAL_META[s.signalType]?.label || s.signalType).join('、')}`);
      }
      if (res.sensitiveHits.length > 0) {
        parts.push(`⚠️ 敏感词告警：${res.sensitiveHits.join('、')}`);
      }
      setNotice(parts.length > 0 ? parts.join(' ｜ ') : '消息已存档，未检测到信号');
      setContent('');
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-1">企微监管 · 信号中心</h2>
      <p className="text-sm text-slate-500 mb-4">会话内容加密存档，规则引擎实时识别续费/增项/流失信号与员工敏感词告警；财务侧应收应付超期账龄预警也汇聚到同一待办入口</p>

      {error && <div className="mb-3"><ErrorBanner message={error} onRetry={() => load()} /></div>}
      {notice && <div className="mb-3 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">{notice}</div>}

      {/* 消息摄入演示 */}
      <form onSubmit={ingest} className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">📥 模拟会话消息摄入（正式版由企微存档 SDK 自动推送）</h3>
        <div className="grid grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs text-slate-500 mb-1">员工 ID</label>
            <input value={staff} onChange={(e) => setStaff(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">客户 ID *</label>
            <input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="customer-01" className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full" required />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-slate-500 mb-1">消息内容 *（试试：合同到期了，别家报价更便宜，我考虑不续了）</label>
            <input value={content} onChange={(e) => setContent(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full" required />
          </div>
        </div>
        <button type="submit" disabled={sending || !canIngest} title={permHint('monitor:ingest')} className="btn btn-primary btn-sm mt-3">
          {sending ? '检测中...' : '摄入并检测'}
        </button>
      </form>

      {/* 财务超期预警（账龄联动） */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="mb-3">
          <ScopeBar value={scope} onChange={setScope} scopeInfo={scopeInfo} />
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-sm font-semibold text-slate-700">
            🔔 财务超期预警
            <span className="ml-2 text-xs font-normal text-slate-400">
              应收 / 应付超信用期未结项，结清后重扫自动销号
            </span>
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={alertSide}
              onChange={(e) => setAlertSide(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600"
            >
              <option value="">应收+应付</option>
              <option value="receivable">仅应收</option>
              <option value="payable">仅应付</option>
            </select>
            <button
              onClick={exportCollectionCsv}
              disabled={exporting || alerts.length === 0}
              title={alerts.length === 0 ? '当前筛选口径下无预警可导出' : '按当前状态/应收应付筛选导出催收单（CSV，含对手方与档案联系方式）'}
              className="btn btn-sm bg-white text-slate-700 border border-slate-200 hover:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
            >
              {exporting ? '导出中…' : '⬇ 导出催收单'}
            </button>
            <button
              onClick={scanAging}
              disabled={scanning || !canScan}
              title={permHint('aging:scan')}
              className="btn btn-primary btn-sm"
            >
              {scanning ? '扫描中…' : '⚡ 立即扫描账龄'}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {ALERT_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setAlertStatus(t.key)}
              className={`px-3 py-1 rounded-lg text-xs ${
                alertStatus === t.key ? 'bg-slate-800 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200'
              }`}
            >
              {t.label}
            </button>
          ))}
          {alerts.length > 0 && (
            <span className="text-xs text-slate-400 ml-auto">
              共 {alerts.length} 条、涉及金额 <span className="font-mono text-red-600">¥{formatAmount(alerts.reduce((s, a) => s + a.amount, 0))}</span>
            </span>
          )}
        </div>

        {alertsLoading ? (
          <div className="py-6 text-center"><Spinner /></div>
        ) : alerts.length === 0 ? (
          <div className="py-8 text-center animate-fade-in-up">
            <div className="w-12 h-12 rounded-2xl bg-brand-50 grid place-items-center text-2xl mx-auto">🔔</div>
            <p className="text-sm font-medium text-slate-600 mt-1">该口径下暂无财务超期预警</p>
            <p className="text-xs text-slate-400">点右上「立即扫描账龄」从往来科目拉出超信用期未结项；也可到报表中心·账龄分析查看分档详情</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[40vh] overflow-auto pr-1">
            {alerts.map((a) => {
              const meta = ALERT_META[a.side] || ALERT_META.receivable;
              const st = ALERT_STATUS[a.status] || ALERT_STATUS.unread;
              return (
                <div key={a.id} className={`rounded-xl border border-slate-100 border-l-4 px-4 py-3 bg-slate-50/60 ${meta.card}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${meta.badge}`}>{meta.label}</span>
                      <span className="text-xs text-slate-400 whitespace-nowrap">{st.label}</span>
                      <span className="text-sm text-slate-700 truncate" title={a.message}>{a.message}</span>
                    </div>
                    <span className="text-sm font-mono font-semibold text-red-600 whitespace-nowrap">¥{formatAmount(a.amount)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <span className="text-xs text-slate-400 truncate">
                      凭证 {a.reference || '—'} · 已挂 {a.agingDays} 天 · 生成于 {a.createdAt.slice(5, 16).replace('T', ' ')}
                      {a.status === 'actioned' && a.actedBy && ` · ${a.actedBy} 已处理`}
                      {a.counterparty && a.counterparty !== '（未指定）' && (
                        <span className="text-slate-500"> · 对手方 {a.counterparty}</span>
                      )}
                    </span>
                    <div className="flex gap-2 shrink-0 items-center">
                      {(a.contact || a.phone) && (
                        <span className="text-xs text-slate-600 whitespace-nowrap" title="来自往来单位档案的催收联系方式">
                          📞 {a.contact || '联系人未填'}
                          {a.phone && <a href={`tel:${a.phone}`} className="ml-1 text-blue-600 hover:underline font-mono">{a.phone}</a>}
                        </span>
                      )}
                      {a.status === 'unread' && (
                        <button onClick={() => markAlert(a.id, 'read')} disabled={!canScan} title={permHint('aging:scan')} className="text-xs px-3 py-1 bg-white text-slate-600 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed">
                          标记已读
                        </button>
                      )}
                      {a.status !== 'actioned' && (
                        <button onClick={() => markAlert(a.id, 'actioned')} disabled={!canScan} title={permHint('aging:scan')} className="text-xs px-3 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 disabled:text-slate-400 disabled:cursor-not-allowed">
                          已处理
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* 信号列表 */}
        <div>
          <div className="flex gap-2 mb-3">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setStatus(t.key)}
                className={`px-3 py-1.5 rounded-lg text-xs ${
                  status === t.key ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="space-y-3">
            {signals.length === 0 ? (
              <EmptyState
                icon="📡"
                title="暂无客户信号"
                hint="在上方摄入会话消息后，规则引擎会自动识别续费 / 增项 / 流失信号；切换状态筛选也可查看更多历史信号"
              />
            ) : (
              signals.map((sig) => {
                const meta = SIGNAL_META[sig.signalType] || SIGNAL_META.renewal;
                return (
                  <div key={sig.id} className={`bg-white rounded-xl shadow-sm border-l-4 p-4 ${meta.card}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${meta.badge}`}>{meta.label}</span>
                        <span className="text-xs text-slate-400">置信度 {(sig.confidence * 100).toFixed(0)}%</span>
                      </div>
                      <span className="text-xs text-slate-400">{sig.createdAt.slice(5, 16).replace('T', ' ')}</span>
                    </div>
                    <p className="text-sm text-slate-700 mb-2 line-clamp-2">“{sig.snippet}”</p>
                    <p className="text-xs text-slate-500 mb-3">💡 建议动作：{sig.suggestedAction}</p>
                    <div className="flex gap-2">
                      {sig.status !== 'read' && sig.status !== 'actioned' && (
                        <button onClick={() => markStatus(sig.id, 'read')} disabled={!canHandle} title={permHint('signal:handle')} className="text-xs px-3 py-1 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed">
                          标记已读
                        </button>
                      )}
                      {sig.status !== 'actioned' && (
                        <button onClick={() => markStatus(sig.id, 'actioned')} disabled={!canHandle} title={permHint('signal:handle')} className="text-xs px-3 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 disabled:text-slate-400 disabled:cursor-not-allowed">
                          已跟进
                        </button>
                      )}
                      {sig.status === 'actioned' && <span className="text-xs text-green-600">✓ 已由 {sig.actedBy || '—'} 跟进</span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* 会话存档 */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">🗂️ 会话存档（最近消息预览，AES-256 加密存储）</h3>
          <div className="space-y-3">
            {sessions.length === 0 ? (
              <div className="py-8 text-center animate-fade-in-up">
                <div className="w-12 h-12 rounded-2xl bg-brand-50 grid place-items-center text-2xl mx-auto">🗂️</div>
                <p className="text-sm font-medium text-slate-600 mt-1">暂无存档会话</p>
                <p className="text-xs text-slate-400">摄入并加密存档后，这里会展示最近消息预览</p>
              </div>
            ) : (
              sessions.map((s) => (
                <div key={s.id} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-center justify-between mb-2 text-xs text-slate-500">
                    <span className="font-medium text-slate-700">{s.staffUserId} ↔ {s.customerUserId}</span>
                    <span>{s.messageCount} 条 · {s.lastMessageAt?.slice(5, 16).replace('T', ' ')}</span>
                  </div>
                  <div className="space-y-1">
                    {s.lastMessages.map((m, i) => (
                      <div key={i} className="text-xs bg-slate-50 rounded px-2 py-1.5 text-slate-600">{m.content}</div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
